<template>
    <div ref="componentWrapper" class="component-wrapper">
        <v-responsive
            class="responsive-box"
            max-height="100%"
            :aspect-ratio="16 / 9"
            :class="bordered && !fullscreen ? 'bordered' : ''"
            :style="computedMaxWidth"
        >
            <div class="small-preview-component-container" :class="{ paused: !isPlaying }">
                <div class="video-click-overlay" @click="togglePlaying" v-show="activeControls">
                    <div class="controls">
                        <div class="big-play-btn">
                            <div class="control-icon pointer" @click.stop="togglePlaying">
                                <font-awesome-icon v-if="!isPlaying" icon="fa-solid fa-play"></font-awesome-icon>
                            </div>
                        </div>

                        <v-btn
                            v-if="!isPlaying && showManualRefresh"
                            style="position: absolute; top: 5px; right: 5px"
                            dark
                            @click.stop="initReload"
                        >
                            <font-awesome-icon v-if="!isPlaying" icon="fa-solid fa-sync-alt fa-lg" class="mr-1 fa-lg"></font-awesome-icon>
                            Refresh</v-btn
                        >
                    </div>
                </div>

                <div
                    class="preview-container"
                    :class="!activeControls ? 'hidden-preview' : ''"
                    ref="previewContainer"
                ></div>
                <div class="preview-overlay" :class="!activeControls ? '' : 'hidden-preview'">
                    <div v-if="loading" class="d-flex flex-column align-items-center">
                        <v-progress-circular indeterminate></v-progress-circular>
                        <p class="mt-2">Loading preview...</p>
                    </div>
                    <div v-else-if="loadError" class="d-flex flex-column align-items-center">
                        <font-awesome-icon
                            style="font-size: 2rem"
                            icon="fa-regular fa-cloud-exclamation"
                        ></font-awesome-icon>
                        <p class="mt-2">Error loading preview.</p>
                        <v-btn small @click="initReload">Reload</v-btn>
                    </div>
                    <div v-else-if="!source" class="d-flex flex-column align-items-center">
                        <font-awesome-icon
                            style="font-size: 2rem"
                            icon="fa-regular fa-cloud-exclamation"
                        ></font-awesome-icon>
                        <p class="mt-2">Preview source not found.</p>
                    </div>
                </div>

                <div v-if="activeControls" class="custom-control-bar controls">
                    <div class="control-icon pointer" @click.stop="togglePlaying">
                        <font-awesome-icon v-if="!isPlaying" icon="fa-solid fa-play"></font-awesome-icon>
                        <font-awesome-icon v-else icon="fa-solid fa-pause"></font-awesome-icon>
                    </div>

                    <v-slider
                        class="mx-2 pointer"
                        @change="handleSliderChange"
                        @start="startDrag"
                        @end="endDrag"
                        v-model="currentTime"
                        :max="duration"
                        :step="dragging ? 1 : 0.1"
                        hide-details
                        color="#ff530d"
                        track-color="#d8d8d8"
                    ></v-slider>

                    <div class="white--text text-caption control-icon">
                        <span>
                            {{ currentTime | formatTimeStamp }}
                        </span>
                        /
                        <span>
                            {{ duration | formatTimeStamp }}
                        </span>
                    </div>
                    <div class="control-icon pointer" @click="togglePreviewFullscreen">
                        <font-awesome-icon v-if="fullscreen" icon="fa-regular fa-compress"></font-awesome-icon>
                        <font-awesome-icon v-else icon="fa-regular fa-expand"></font-awesome-icon>
                    </div>
                </div>
            </div>
        </v-responsive>
    </div>
</template>
<script>
import { Preview } from '@creatomate/preview';
import { debounce } from 'lodash';
import moment from 'moment';

export default {
    data() {
        return {
            preview: null,
            loading: true,
            loadError: false,
            resizeObserver: null,
            wrapperHeight: 0,
            resizeObserver: null,
            fullscreen: false,
            currentTime: 0,
            dragging: false,
            duration: 0,
            mounted: false,
            isPlaying: false,
        };
    },
    props: {
        source: {
            type: Object,
            default: null,
        },
        template: {
            type: Boolean,
            default: false,
        },
        modifications: {
            type: Object,
            required: false,
            default: null,
        },
        bordered: {
            type: Boolean,
            default: false,
        },
        withResizeObserver: {
            type: Boolean,
            default: true,
        },
        showManualRefresh: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        computedMaxWidth() {
            if (!this.wrapperHeight) return {};

            // 12  px buffer removes odd offset bars on sides
            const calculatedMaxWidth = this.wrapperHeight * (16 / 9) - 12;

            return {
                maxWidth: `${calculatedMaxWidth}px`,
            };
        },
        activeControls() {
            return !this.loadError && !this.loading;
        },
    },
    watch: {
        async source() {
            if (this.source) {
                await this.debouncedInitPreview();
            }

            if (this.preview?.setTime && this.currentTime > 0) {
                await this.preview.setTime(this.currentTime);
            }
            if (this.isPlaying) {
                this.preview.play();
            }
        },
        async modifications() {
            if (this.source) {
                await this.debouncedInitPreview();
            }
            if (this.preview?.setTime && this.currentTime > 0) {
                await this.preview.setTime(this.currentTime);
            }
            if (this.isPlaying) {
                this.preview.play();
            }
        },
    },
    mounted() {
        if (this.$props.withResizeObserver) {
            this.setupResizeObserver();
            this.initFullscreenListeners();
        }
        if (this.source) {
            this.debouncedInitPreview();
        }
    },
    created() {
        this.debouncedInitPreview = debounce(this.initPreview, 200);
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.removeFullscreenListeners();
    },
    filters: {
        formatTimeStamp(seconds) {
            const duration = moment.duration(seconds, 'seconds');
            const hours = duration.hours();
            const minutes = duration.minutes();
            const secs = duration.seconds();

            const formattedMinutes = String(minutes).padStart(2, '0');
            const formattedSeconds = String(secs).padStart(2, '0');
            if (hours > 0) {
                return `${hours}:${formattedMinutes}:${formattedSeconds}`;
            }
            return `${formattedMinutes}:${formattedSeconds}`;
        },
    },
    methods: {
        setTime(val) {
            this.handleSliderChange(val);
        },
        startDrag() {
            this.dragging = true;

            this.pauseVideo();
        },
        endDrag() {
            this.dragging = false;
        },
        handleSliderChange(number) {
            if (!this.preview) return;

            this.preview.setTime(number);
        },
        setupResizeObserver() {
            if (!this.$refs.componentWrapper) return;

            // this.debouncedUpdateHeight = debounce(height => {
            //     this.wrapperHeight = height;
            // }, 16);

            // this.resizeObserver = new ResizeObserver(entries => {
            //     for (let entry of entries) {
            //         this.debouncedUpdateHeight(entry.contentRect.height);
            //     }
            // });

            this.resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries) {
                    const newHeight = entry.contentRect.height;
                    // Only update if height actually changed to avoid unnecessary reactivity
                    if (Math.abs(this.wrapperHeight - newHeight) > 0.5) {
                        this.wrapperHeight = newHeight;
                    }
                }
            });

            this.resizeObserver.observe(this.$refs.componentWrapper);

            this.wrapperHeight = this.$refs.componentWrapper.clientHeight;
        },
        initReload() {
            this.reset();
            this.$emit('reload');
        },
        reset() {
            this.loading = true;
            this.loadError = false;
        },
        async playVideo() {
            if (!this.preview) return;

            this.isPlaying = true;

            if (!this.preview.ready) {
                this.preview.onReady = async () => {
                    await this.preview.play();
                };
            } else {
                await this.preview.play();
            }
        },
        async pauseVideo() {
            if (!this.preview) return;

            this.isPlaying = false;
            if (!this.preview.ready) {
                this.preview.onReady = async () => {
                    await this.preview.pause();
                };
            } else {
                await this.preview.pause();
            }
        },
        togglePlaying() {
            !this.isPlaying ? this.playVideo() : this.pauseVideo();
        },
        togglePreviewFullscreen() {
            const previewEl = this.$refs.componentWrapper;

            if (!previewEl) return;

            if (!document.fullscreenElement) {
                this.enterFullscreen(previewEl);
                this.fullscreen = true;
            } else {
                this.exitFullscreen();
                this.fullscreen = false;
            }
        },
        enterFullscreen(element) {
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                // Safari
                element.webkitRequestFullscreen();
            } else if (element.mozRequestFullScreen) {
                // Firefox
                element.mozRequestFullScreen();
            } else if (element.msRequestFullscreen) {
                // IE/Edge
                element.msRequestFullscreen();
            }
        },

        exitFullscreen() {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                // Safari
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                // Firefox
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                // IE/Edge
                document.msExitFullscreen();
            }
        },
        async setPreviewData(preview) {
            if (!preview || !preview.ready) return;

            await preview.setControls(false);

            preview.onTimeChange = time => {
                if (!this.dragging) {
                    if (!this.isPlaying) {
                        console.log('Preview player time change w/o drag', time);
                    }
                    this.currentTime = time;
                    this.$emit('time-update', time);
                }
            };

            try {
                this.reset();

                if (this.template) {
                    await preview.loadTemplate(this.source);
                } else {
                    const json = this.source;
                    json.render_scale = 0.5;
                    await preview.setSource(json);
                }

                if (this.modifications) {
                    const mods = this.modifications;
                    await preview.setModifications(mods);
                }

                try {
                    if (this.source?.duration) {
                        const parsedDur = parseFloat(this.source?.duration?.replace(' s', ''));
                        this.duration = Math.round(parsedDur);
                    }
                } catch (err) {
                    console.log('error parsing duration', err);
                }

                this.loadError = false;
            } catch (error) {
                this.loadError = true;
                console.error('PREVIEW ERROR: ', error);
            } finally {
                this.loading = false;
            }
        },
        initFullscreenListeners() {
            document.addEventListener('fullscreenchange', this.handleFullscreenChange);
            document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange); // Safari
            document.addEventListener('mozfullscreenchange', this.handleFullscreenChange); // Firefox
            document.addEventListener('MSFullscreenChange', this.handleFullscreenChange); // IE/Edge
        },
        removeFullscreenListeners() {
            document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
        },
        handleFullscreenChange() {
            const fullscreenElement =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement;

            this.fullscreen = fullscreenElement === this.$refs.componentWrapper;
        },
        async initPreview() {
            const start = performance.now();
            this.reset();

            const el = this.$refs.previewContainer;

            if (el) {
                if (!this.preview) {
                    const preview = await new Preview(el, 'player', process.env.VUE_APP_CREATOMATE_KEY);
                    this.preview = preview;
                }

                if (!this.preview.ready) {
                    this.preview.onReady = async () => {
                        await this.setPreviewData(this.preview);
                    };
                } else {
                    await this.setPreviewData(this.preview);
                }
            }
        },
    },
};
</script>
<style lang="scss">
.component-wrapper {
    height: 100%;
    max-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    .small-preview-component-container {
        position: relative;
        height: 100%;

        &:hover .controls,
        &.paused .controls {
            opacity: 1 !important;
            transition-delay: 0ms !important;
        }

        .video-click-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            margin: auto;
            z-index: 1;
            background: transparent;
            cursor: default;
            display: flex;
            align-items: center;
            justify-content: center;

            .controls {
                opacity: 0.4;
                transition: opacity 400ms ease;
                transition-delay: 800ms;

                .control-icon {
                    font-size: 4.5rem !important;
                }
            }
        }

        .hidden-preview {
            opacity: 0;
            z-index: -2;
        }

        .custom-control-bar {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            margin: auto;
            padding: 0 8px;
            display: flex;
            align-items: center;
            z-index: 2;
            background: linear-gradient(0deg, rgba(0, 0, 0, 0.4), transparent);

            .v-slider__track-container {
                height: 4px;
            }

            .v-slider {
                cursor: pointer;
            }
        }

        .controls {
            opacity: 0;
            transition: opacity 400ms ease;
            transition-delay: 800ms;

            .control-icon {
                color: white;
                padding: 4px 8px;

                font-size: 1.2rem;
            }
        }
    }

    .bordered {
        border-radius: 8px;
        border: 8px solid white;
    }

    .responsive-box {
        background-color: black;
        // border: 5px solid white;

        height: auto;
        width: 100%;
    }
    .preview-container {
        aspect-ratio: 16/9;
        opacity: 1;
        transition: 0.5s;
        height: 100%;
        // background-color: black;
        width: 100%;
        max-width: 100%;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
    }

    .preview-overlay {
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;
        background-color: black;
        color: white;
    }
}
</style>
